import { SECOND } from '@lib/utils/time';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResendCooldownOptions {
  /** Cooldown length in seconds. Defaults to 60. */
  durationSeconds?: number;
  /**
   * Seed the cooldown from a pre-existing start timestamp (epoch ms). Read once
   * on mount — pass a store/persisted value that should survive remount/reload
   * (e.g. `userStore.lastResendTime`). Later cooldowns are armed via `start()`.
   * Leave unset for a purely in-memory cooldown.
   */
  initialStartedAt?: number;
}

interface ResendCooldown {
  /** Whole seconds remaining, 0 when idle. Render as `Resend in ${secondsLeft}s`. */
  secondsLeft: number;
  /** True while `secondsLeft > 0`. */
  active: boolean;
  /** Arm the cooldown from now. Call after a successful send. */
  start: () => void;
}

/**
 * Shared resend / countdown-timer cooldown for the auth flows (magic-link
 * request, password-reset request, verification-email resend). Each call site
 * gets its own independent timer — they are intentionally NOT coupled to a
 * single shared timestamp.
 *
 * Remaining time is derived from a wall-clock delta, so it stays accurate even
 * if the tab is backgrounded and the interval is throttled.
 */
export function useResendCooldown(opts: UseResendCooldownOptions = {}): ResendCooldown {
  const { durationSeconds = 60, initialStartedAt = 0 } = opts;
  const durationMs = durationSeconds * SECOND;

  const compute = useCallback(
    (start: number) =>
      start === 0 ? 0 : Math.max(0, Math.ceil((start + durationMs - Date.now()) / SECOND)),
    [durationMs],
  );

  const [startedAt, setStartedAt] = useState<number>(initialStartedAt);
  // Seed lazily so a cooldown already in effect (a recent initialStartedAt) is
  // reflected on the very first render, not one frame late.
  const [secondsLeft, setSecondsLeft] = useState(() => compute(initialStartedAt));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const left = compute(startedAt);
    setSecondsLeft(left);
    // Only run the interval while time actually remains — an already-expired
    // (or absent) seed should arm nothing.
    if (left <= 0) return undefined;

    intervalRef.current = setInterval(() => {
      const next = compute(startedAt);
      setSecondsLeft(next);
      if (next <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, SECOND);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [startedAt, compute]);

  const start = useCallback(() => setStartedAt(Date.now()), []);

  return { secondsLeft, active: secondsLeft > 0, start };
}
